import React, { useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import { formatRegionName, ShellFont, toolTipFormatter } from '../../../utils';
import Chart from '../../Chart/Chart';
import { CHART_AXIS_STYLE, one, point5, POWER_DISCLAIMER } from '../../../constants';
import { ChartData, PointsData } from '../../../Types/types';
import { StyledDiv, ChartContainer } from '../ScenerioModellingChart/index.styles';
import { currentYear } from '../../../utils/utils';
import { useAppSelector } from '../../../redux/hooks';
import { SNCI } from '../../../api/constants';
import { getActualLastYearIndex } from '../../CommodityOverview/Utils';

interface ScenerioModellingChartProps {
  netCarbonIntensity: ChartData[];
}

const NetCarbonIntensity: React.FC<ScenerioModellingChartProps> = ({ netCarbonIntensity }) => {
  const { loggedInUserRegion, isActualsRefresh } = useAppSelector((state) => state.dashboard);
  const baseCase = {
    ...netCarbonIntensity[0],
    color: '#E4494C',
    dashStyle: 'Dash',
    data: netCarbonIntensity[0]?.data.map((item) => item.CHANGED_REPORTED_GROUP_NCI),
    lineWidth: 1.5,
  };
  const senarioCase = {
    ...netCarbonIntensity[1],
    color: '#0097A9',
    dashStyle: 'Solid',
    data: netCarbonIntensity[1]?.data.map((item) => item.CHANGED_REPORTED_GROUP_NCI),
    lineWidth: 6,
    lineColor: 'rgba(0, 151, 169, 0.2)',
  };
  const columeRange = {
    ...netCarbonIntensity[2],
    color: '#0097A9',
    dashStyle: 'Solid',
    showInLegend: false,
  };
  const [indexOfLastActuals, setIndexOfLastActuals] = useState(-1);
  const reducedArray = netCarbonIntensity[0].data.map(({ YEAR }) => ({ YEAR }));
  const uniqueYears = reducedArray.map(({ YEAR }) => ({
    year: Number(YEAR),
    type: Number(YEAR) < currentYear ? 'actuals' : 'plan',
  }));

  useEffect(() => {
    setIndexOfLastActuals(getActualLastYearIndex(uniqueYears))
  }, [uniqueYears]);

  const regionDisplay =
    loggedInUserRegion.toLowerCase() === 'europe'
      ? 'Europe/Africa'
      : formatRegionName(loggedInUserRegion);

  const nciChartTitle = loggedInUserRegion ? `${SNCI} for ${regionDisplay}` : `${SNCI}`;
  let lowerLimit = senarioCase.data && Math.min(...(senarioCase?.data as number[]));
  let upperLimit = senarioCase.data && Math.max(...(senarioCase?.data as number[]));

  lowerLimit = Math.floor(lowerLimit / 10) * 10;
  upperLimit = Math.ceil(upperLimit / 10) * 10;
  const options: Highcharts.Options = {
    chart: {
      type: 'line',
      zooming: {
        type: 'xy',
      },
      height: 500,
      marginBottom: 100,
      style: {
        fontFamily: 'Shell Font, Arial, sans-serif',
      },
    },
    title: {
      text: nciChartTitle,
      align: 'left',
    },
    subtitle: {
      align: 'left',
      text: 'An overview of Base Case NCI Target vs Scenario NCI Target.',
      style: {
        fontSize: '14px',
        fontFamily: ShellFont,
      },
    },
    legend: {
      itemStyle: {
        fontSize: '12px',
        fontWeight: 'light',
        color: '#757575',
        fontFamily: 'Shell Font, Arial, sans-serif',
      },
    },
    yAxis: {
      title: {
        useHTML: true,
        text: 'NCI Targets (gCO2e/MJ)',
        style: CHART_AXIS_STYLE,
      },
      min: lowerLimit,
      max: upperLimit,
    },
    xAxis: {
      categories: uniqueYears.map((x: { year: number }) => x.year.toString()),
      plotBands:
        indexOfLastActuals !== -1
          ? [
            {
              color: 'rgba(128, 128, 128, 0.3)', // Gray background for historical section
              from: -point5,
              to: isActualsRefresh ? indexOfLastActuals + one : indexOfLastActuals,
              label: {
                text: 'Actuals',
                style: {
                  fontFamily: ShellFont,
                  fontSize: '11px',
                  fontWeight: '600',
                  lineHeight: '16px',
                  letterSpacing: '0em',
                  textAlign: 'right',
                  color: '#343434',
                },
                verticalAlign: 'bottom', // Label below the y axis
                y: 40, // Adjust label position
              },
            },

            {
              color: 'white', // Gray background for historical section
              from: uniqueYears.length - indexOfLastActuals,
              to: uniqueYears.length - 1 + point5,
              label: {
                text: 'Plan',
                style: {
                  fontFamily: ShellFont,
                  fontSize: '11px',
                  fontWeight: '600',
                  lineHeight: '16px',
                  letterSpacing: '0em',
                  textAlign: 'right',
                  color: '#343434',
                },
                verticalAlign: 'bottom', // Label below the y axis
                y: 40, // Adjust label position
              },
            },
          ]
          : [
            {
              color: 'white', // Gray background for historical section
              from: -point5,
              to: uniqueYears.length - 1 + point5, // To final year
              label: {
                text: 'Plan',
                style: {
                  fontFamily: ShellFont,
                  fontSize: '11px',
                  fontWeight: '600',
                  lineHeight: '16px',
                  letterSpacing: '0em',
                  textAlign: 'right',
                  color: '#343434',
                },
                verticalAlign: 'bottom', // Label below the y axis
                y: 40, // Adjust label position
              },
            },
          ],
    },
    tooltip: {
      shared: true,
      headerFormat: '<span style="font-size:12px"><b>{point.key}</b></span><br>',
      valueDecimals: 2,
      formatter() {
        return toolTipFormatter(this as unknown as PointsData, true);
      },
    },
    plotOptions: {
      area: {
        stacking: 'normal',
        lineColor: '#666666',
        lineWidth: 1,
        marker: {
          enabled: false,
          symbol: 'circle',
          radius: 2,
          states: {
            hover: {
              enabled: true,
            },
          },
        },
      },
    },
    series: [
      { ...columeRange, inverted: true, showInLegend: false },
      senarioCase,
      baseCase,
    ] as Highcharts.SeriesOptionsType[],
  };
  return (
    <StyledDiv>
      <ChartContainer>
        <Chart
          chartKey={'nci-chart'}
          highcharts={Highcharts}
          options={options}
          source={'Scenario Table'}
          data-testid='nci-chart'
          disclaimer={[POWER_DISCLAIMER]}
        />
        <div style={{ display: 'none' }} data-testid='nci-hidden-for-automation'>
          {JSON.stringify(netCarbonIntensity)}
        </div>
      </ChartContainer>
    </StyledDiv>
  );
};

export default NetCarbonIntensity;
